import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_PAGE_SIZE = 20

/**
 * Client-side incremental reveal for large search result lists.
 * Pass enabled=false to show all items at once (e.g. bounded date-range searches).
 */
export function useInfiniteScrollResults(
  items,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true
) {
  const [visibleCount, setVisibleCount] = useState(pageSize)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef(null)
  const total = items?.length ?? 0

  useEffect(() => {
    setVisibleCount(enabled ? pageSize : total)
    setLoadingMore(false)
  }, [items, pageSize, enabled, total])

  const loadMore = useCallback(() => {
    if (!enabled || visibleCount >= total) return
    setLoadingMore(true)
    window.requestAnimationFrame(() => {
      setVisibleCount(prev => Math.min(prev + pageSize, total))
      setLoadingMore(false)
    })
  }, [enabled, visibleCount, total, pageSize])

  useEffect(() => {
    if (!enabled) return undefined

    const node = sentinelRef.current
    if (!node || visibleCount >= total) return undefined

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          loadMore()
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled, loadMore, visibleCount, total])

  const visibleItems = enabled
    ? (items ?? []).slice(0, visibleCount)
    : (items ?? [])
  const hasMore = enabled && visibleCount < total

  return {
    visibleItems,
    hasMore,
    loadingMore,
    sentinelRef,
    total,
    enabled,
  }
}

export default useInfiniteScrollResults
